import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Ticket, X } from 'lucide-react';
import { toast } from 'sonner';
import { getActiveTimezone } from '@/utils/timezone';
import {
  supportTicketApi,
  SUPPORT_TICKET_MAX_IMAGES,
  SUPPORT_TICKET_IMAGE_MAX_MB,
  SUPPORT_TICKET_VIDEO_MAX_MB,
} from '@/services/supportTicketApi';
import SimpleSelect, { type SimpleSelectOption } from '@/components/common/SimpleSelect';
import TablePagination from '@/components/common/TablePagination';
import { MultiImageUpload } from '@/components/common/MultiImageUpload';
import TicketVideoPicker from '@/components/support/TicketVideoPicker';

const CATEGORY_OPTIONS: SimpleSelectOption[] = [
  { value: 'GENERAL', label: 'General' },
  { value: 'BILLING', label: 'Billing' },
  { value: 'TECHNICAL', label: 'Technical' },
  { value: 'BUG', label: 'Bug Report' },
];

const PRIORITY_OPTIONS: SimpleSelectOption[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'bg-red-100 text-red-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  AWAITING_REPLY: 'bg-amber-100 text-amber-700',
  RESOLVED: 'bg-green-100 text-green-700',
  CLOSED: 'bg-gray-100 text-gray-600',
};

const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'bg-gray-100 text-gray-600',
  MEDIUM: 'bg-blue-100 text-blue-700',
  HIGH: 'bg-orange-100 text-orange-700',
  URGENT: 'bg-red-100 text-red-700',
};

const PAGE_SIZE = 20;
const SUBJECT_MAX = 200;
const DESCRIPTION_MAX = 2000;

const EMPTY_FORM = { subject: '', description: '', category: 'GENERAL', priority: 'MEDIUM' };

export default function SupportTickets() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Screenshots (up to SUPPORT_TICKET_MAX_IMAGES) + one optional screen recording.
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [video, setVideo] = useState<File | null>(null);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['my-support-tickets', page],
    queryFn: () => supportTicketApi.list({ page, limit: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  const tickets: any[] = data?.data || [];
  const meta = data?.meta;

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImages([]);
    setPreviews([]);
    setVideo(null);
    setFieldErrors({});
  };

  const createMutation = useMutation({
    mutationFn: () => supportTicketApi.create(form, { images, video }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-support-tickets'] });
      setShowCreate(false);
      resetForm();
      toast.success('Ticket submitted successfully');
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to create ticket'),
  });

  const handleCreate = () => {
    const errors: Record<string, string> = {};
    const subject = form.subject.trim();
    const description = form.description.trim();
    if (!subject) errors.subject = 'Subject is required';
    else if (subject.length > SUBJECT_MAX) errors.subject = `Subject must be under ${SUBJECT_MAX} characters`;
    if (!description) errors.description = 'Description is required';
    else if (description.length > DESCRIPTION_MAX) errors.description = `Description must be under ${DESCRIPTION_MAX} characters`;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    createMutation.mutate();
  };

  const closeCreate = () => {
    if (createMutation.isPending) return;
    setShowCreate(false);
    resetForm();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-sm flex-shrink-0">
            <Ticket className="h-5 w-5" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold tracking-tight text-gray-900 m-0">Support Tickets</h1>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 whitespace-nowrap"
        >
          <Plus className="h-4 w-4" /> New Ticket
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => navigate(`/support-tickets/${ticket.id}`)}
                className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-indigo-600">{ticket.ticketNo}</span>
                    <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${STATUS_COLORS[ticket.status]}`}>
                      {ticket.status.replace(/_/g, ' ')}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${PRIORITY_COLORS[ticket.priority]}`}>
                      {ticket.priority}
                    </span>
                  </div>
                  <p className="font-medium text-gray-900 text-sm truncate">{ticket.subject}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {new Date(ticket.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: getActiveTimezone() })}
                    {ticket._count?.messages > 0 && ` · ${ticket._count.messages} messages`}
                    {ticket.screenshots?.length > 0 && ` · ${ticket.screenshots.length} attachments`}
                  </p>
                </div>
              </div>
            ))}
            {tickets.length === 0 && (
              <div className="px-6 py-12 text-center text-gray-400">
                No tickets yet. Create one to get help from our team.
              </div>
            )}
          </div>
        )}
        {meta && meta.totalPages > 1 && (
          <TablePagination
            currentPage={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={meta.limit}
            onPageChange={setPage}
          />
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeCreate}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Create Support Ticket</h3>
              <button onClick={closeCreate} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {fieldErrors.subject && <p className="text-red-500 text-xs mt-1">{fieldErrors.subject}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <SimpleSelect
                    value={form.category}
                    onChange={(v) => setForm({ ...form, category: v })}
                    options={CATEGORY_OPTIONS}
                    aria-label="Category"
                    wrapperClassName="w-full min-w-0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <SimpleSelect
                    value={form.priority}
                    onChange={(v) => setForm({ ...form, priority: v })}
                    options={PRIORITY_OPTIONS}
                    aria-label="Priority"
                    wrapperClassName="w-full min-w-0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={5}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe your issue in detail..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {fieldErrors.description && <p className="text-red-500 text-xs mt-1">{fieldErrors.description}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Screenshots <span className="text-gray-400 font-normal">(optional, up to {SUPPORT_TICKET_MAX_IMAGES} images)</span>
                </label>
                <MultiImageUpload
                  images={images}
                  previews={previews}
                  onChange={(files, urls) => { setImages(files); setPreviews(urls); }}
                  maxImages={SUPPORT_TICKET_MAX_IMAGES}
                  accept="image/jpeg,image/png,image/webp"
                />
                <p className="text-[11px] text-gray-400 mt-1">JPG, PNG or WebP · max {SUPPORT_TICKET_IMAGE_MAX_MB} MB each</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Screen recording <span className="text-gray-400 font-normal">(optional, 1 video up to {SUPPORT_TICKET_VIDEO_MAX_MB} MB)</span>
                </label>
                <TicketVideoPicker video={video} onChange={setVideo} />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={closeCreate} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!form.subject.trim() || !form.description.trim() || createMutation.isPending}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {createMutation.isPending ? (video ? 'Uploading video...' : 'Submitting...') : 'Submit Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
